import React from 'react';
import type { Card } from '../game/types';
import { getDeckRemoveBlockedReason } from '../game/deckRules';
import CardView from './CardView';

import keyIcon from '../../assets/key_icon.png';
import teethIcon from '../../assets/teeth_icon.png';
import potionIcon from '../../assets/potion_icon.png';
import torchIcon from '../../assets/torch_icon.png';
import buttonWell from '../../assets/button_well.png';

type Loot = {
  teeth: number;
  keys?: number;
  potions?: number;
  torches?: number;
};


export default function Reward({
  choices,
  deck,
  loot,
  onPickAdd,
  onPickRemove,
  onEditDeck,
  minDeckSize = 8,
  requireBothKinds = true,
}: {
  choices: Card[];
  deck: Card[];
  loot?: Loot;
  onPickAdd: (cardId: string) => void;
  onPickRemove?: (cardId: string) => void;
  onEditDeck: () => void;
  minDeckSize?: number;
  requireBothKinds?: boolean;
}) {
  const [mode, setMode] = React.useState<'add' | 'remove'>('add');
  const [removeNotice, setRemoveNotice] = React.useState<string>('');
  const canRemoveReward = typeof onPickRemove === 'function';

  React.useEffect(() => {
    if (!canRemoveReward && mode !== 'add') setMode('add');
  }, [canRemoveReward, mode]);

  React.useEffect(() => {
    setRemoveNotice('');
  }, [mode, deck.length, minDeckSize, requireBothKinds]);

  const removableDeck = React.useMemo(
    () => deck.filter((c) => !getDeckRemoveBlockedReason(deck, c, minDeckSize, requireBothKinds)),
    [deck, minDeckSize, requireBothKinds]
  );

  const removeModeBlockedReason = React.useMemo(() => {
    if (!canRemoveReward) return '';
    if (deck.length <= minDeckSize) {
      return `Remove option available once your deck has more than ${minDeckSize} cards.`;
    }
    if (removableDeck.length === 0) {
      return requireBothKinds
        ? 'You must keep at least 1 Physical card and 1 Magic card.'
        : 'No cards can be removed right now.';
    }
    return '';
  }, [canRemoveReward, deck.length, minDeckSize, removableDeck.length, requireBothKinds]);

  React.useEffect(() => {
    if (mode === 'remove' && removeModeBlockedReason) setMode('add');
  }, [mode, removeModeBlockedReason]);

  const hasAnyLoot =
    !!loot &&
    ((loot.teeth ?? 0) > 0 || (loot.keys ?? 0) > 0 || (loot.potions ?? 0) > 0 || (loot.torches ?? 0) > 0);

  const headerText =
    mode === 'remove'
      ? 'Choose one card from your deck to remove.'
      : 'Choose a reward to shape your deck. Pick one of these cards to add:';

  const leftActionLabel = mode === 'remove' ? 'Card reward' : 'Remove';
  const leftActionTitle =
    mode === 'remove'
      ? 'Go back to the card reward choices'
      : removeModeBlockedReason || 'Remove 1 card from your deck';

  const renderLootChip = (icon: string, label: string, amount: number | undefined) => {
    if (!amount) return null;
    return (
      <div className="rewardLootChip" key={label}>
        <img src={icon} alt="" aria-hidden="true" draggable={false} className="rewardLootIcon" />
        <span>
          +{amount} {label}
        </span>
      </div>
    );
  };

  return (
    <div className="rewardScene">
      <div className="rewardCard" style={{ fontFamily: 'Karma, ui-serif, Georgia, serif' }}>

        <div className="rewardInner">
          <div className="rewardHeaderBlock">
            <div className="rewardTitle" style={{ fontFamily: 'DarkNouveau, serif' }}>
              Victory!
            </div>
            <div className="rewardSub">{headerText}</div>
          </div>

          {mode === 'remove' && canRemoveReward ? (
            <>
              <div className="rewardRemoveGrid">
                {deck.map((c) => {
                  const blockedReason = getDeckRemoveBlockedReason(deck, c, minDeckSize, requireBothKinds);
                  const disabled = !!blockedReason;

                  return (
                    <div
                      key={c.id}
                      className={`rewardRemoveCard${disabled ? ' disabled' : ''}`}
                      role="button"
                      tabIndex={0}
                      aria-disabled={disabled}
                      onClick={() => {
                        if (blockedReason) {
                          setRemoveNotice(blockedReason);
                          return;
                        }
                        setRemoveNotice('');
                        onPickRemove?.(c.id);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          if (blockedReason) {
                            setRemoveNotice(blockedReason);
                            return;
                          }
                          setRemoveNotice('');
                          onPickRemove?.(c.id);
                        }
                      }}
                      title={blockedReason ?? 'Remove this card'}
                    >
                      <CardView card={c} />
                    </div>
                  );
                })}
              </div>

              {removeNotice && (
                <div className="rewardEmpty rewardInlineNotice" role="status" aria-live="polite">
                  {removeNotice}
                </div>
              )}

              {removableDeck.length === 0 && (
                <div className="rewardEmpty rewardInlineNotice">No cards can be removed right now.</div>
              )}
            </>
          ) : (
            <>
              <div className="rewardChoices">
                {choices.map((c) => (
                  <div
                    key={c.id}
                    className="rewardChoice"
                    role="button"
                    tabIndex={0}
                    onClick={() => onPickAdd(c.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onPickAdd(c.id);
                      }
                    }}
                  >
                    <CardView card={c} />
                  </div>
                ))}

                {choices.length === 0 && (
                  <div className="rewardEmpty rewardInlineNotice">
                    No eligible cards left to add. Choose <b>Skip</b>.
                  </div>
                )}
              </div>
            </>
          )}

          {loot ? (
            hasAnyLoot ? (
              <div className="rewardLootSection">
                <div className="rewardLootLabel">Resource rewards:</div>
                <div className="rewardLootRow">
                  {renderLootChip(teethIcon, 'Tooth', loot.teeth)}
                  {renderLootChip(keyIcon, 'Key', loot.keys)}
                  {renderLootChip(potionIcon, 'Potion', loot.potions)}
                  {renderLootChip(torchIcon, 'Torch', loot.torches)}
                </div>
              </div>
            ) : (
              <div className="rewardEmpty rewardInlineNotice">No loot this time.</div>
            )
          ) : null}

          <div className="rewardFooterActions">
            <div className="rewardSplitAction" style={{ aspectRatio: '553 / 159' }}>
              <img
                src={buttonWell}
                alt=""
                aria-hidden="true"
                draggable={false}
                className="rewardSplitActionBg"
              />

              <button
                type="button"
                disabled={mode !== 'remove' && !!removeModeBlockedReason}
                onClick={() => {
                  if (mode === 'remove') {
                    setMode('add');
                    return;
                  }
                  if (removeModeBlockedReason) return;
                  setMode('remove');
                }}
                title={leftActionTitle}
                className="rewardSplitButton rewardSplitButtonLeft"
                style={{ fontFamily: 'Karma, ui-serif, Georgia, serif' }}
              >
                <span>{leftActionLabel}</span>
              </button>

              <button
                type="button"
                onClick={onEditDeck}
                className="rewardSplitButton rewardSplitButtonRight"
                style={{ fontFamily: 'Karma, ui-serif, Georgia, serif' }}
              >
                <span>Skip</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
